import React, { useEffect, useState } from "react"
import { LineChart } from '@mui/x-charts/LineChart'
import {
	Container,
	Grid,
	Typography,
	IconButton,
	Box,
	Button,
	Select,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	InputLabel,
	FormControl,
	FormControlLabel,
	Switch,
	Popover,
	Stack,
	Divider,
	TextField, useTheme
} from "@mui/material"
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { ArrowBack, CalendarMonth, Check, Tune } from "@mui/icons-material"
import moment from "moment"
import { useNavigate } from "react-router-dom"
import LeftRight from "../LeftRight"
import { doGet } from "../../util/do-fetch"
import LeftRightCenter from "../LeftRightCenter"
import getSupabase from "../../util/supabase-utils"
import MetricsManager from "./MetricsManager"

const PURCHASE_TOTAL_METRIC = {
	metric_key: 'purchase_total',
	label: 'Purchase Total ($)',
	color: '#4caf50',
}

const dateRangeShortcuts = [
	{
		label: "This Week",
		getValue: () => [moment().startOf('week'), moment()]
	},
	{
		label: "Last Week",
		getValue: () => [
			moment().subtract(1, 'week').startOf('week'),
			moment().subtract(1, 'week').endOf('week')
		]
	},
	{
		label: "This Month",
		getValue: () => [moment().startOf('month'), moment()]
	},
	{
		label: "Last Month",
		getValue: () => [
			moment().subtract(1, 'month').startOf('month'),
			moment().subtract(1, 'month').endOf('month')
		]
	},
	{
		label: "Last 3 Months",
		getValue: () => [moment().subtract(3, 'months'), moment()]
	},
	{
		label: "Last 6 Months",
		getValue: () => [moment().subtract(6, 'months'), moment()]
	},
	{
		label: "Last Year",
		getValue: () => [moment().subtract(1, 'year'), moment()]
	},
	{
		label: "All Time",
		getValue: () => [moment('2022-01-01'), moment()]
	}
]

// Custom DateRangePicker component using two DatePickers
const CustomDateRangePicker = ({ value, onChange, shortcuts, size = 'small' }) => {
	const [anchorEl, setAnchorEl] = useState(null)
	const [tempRange, setTempRange] = useState(value)

	const handleClick = (event) => {
		setTempRange(value)
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	const handleApply = () => {
		onChange(tempRange)
		handleClose()
	}

	const handleShortcut = (shortcut) => {
		const range = shortcut.getValue()
		setTempRange(range)
		onChange(range)
		handleClose()
	}

	const formatDateRange = (range) => {
		if (!range[0] || !range[1]) return 'Select dates'
		return `${range[0].format('MMM D, YYYY')} - ${range[1].format('MMM D, YYYY')}`
	}

	const open = Boolean(anchorEl)

	return (
		<>
			<Button
				variant="outlined"
				size={size}
				onClick={handleClick}
				startIcon={<CalendarMonth />}
				sx={{ minWidth: 260, justifyContent: 'flex-start' }}
			>
				{formatDateRange(value)}
			</Button>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
			>
				<Box sx={{ p: 2, width: 400 }}>
					<Stack spacing={2}>
						{shortcuts && (
							<>
								<Typography variant="subtitle2">Quick Select</Typography>
								<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
									{shortcuts.map((shortcut) => (
										<Button
											key={shortcut.label}
											size="small"
											variant="outlined"
											onClick={() => handleShortcut(shortcut)}
										>
											{shortcut.label}
										</Button>
									))}
								</Stack>
								<Divider />
							</>
						)}
						<Typography variant="subtitle2">Custom Range</Typography>
						<Stack direction="row" spacing={2}>
							<DatePicker
								label="Start Date"
								value={tempRange[0]}
								onChange={(newValue) => setTempRange([newValue, tempRange[1]])}
								slotProps={{ textField: { size: 'small' } }}
							/>
							<DatePicker
								label="End Date"
								value={tempRange[1]}
								onChange={(newValue) => setTempRange([tempRange[0], newValue])}
								slotProps={{ textField: { size: 'small' } }}
							/>
						</Stack>
						<Stack direction="row" spacing={1} justifyContent="flex-end">
							<Button size="small" onClick={handleClose}>Cancel</Button>
							<Button size="small" variant="contained" onClick={handleApply}>Apply</Button>
						</Stack>
					</Stack>
				</Box>
			</Popover>
		</>
	)
}

const Conversions = () => {
	const navigate = useNavigate()
	const theme = useTheme()
	const [data, setData] = useState([])
	const [compareData, setCompareData] = useState([])
	const [categories, setCategories] = useState([])
	const [granularity, setGranularity] = useState('day')
	const [categoryId, setCategoryId] = useState('all')
	const [dateRange, setDateRange] = useState([moment().startOf('month'), moment()])
	const [compareDateRange, setCompareDateRange] = useState([
		moment().subtract(1, 'month').startOf('month'),
		moment().subtract(1, 'month').endOf('month')
	])
	const [compareMode, setCompareMode] = useState(false)
	const [selectedMetric, setSelectedMetric] = useState('registrations')

	// Custom metrics state
	const [metricDefinitions, setMetricDefinitions] = useState([])
	const [activeMetricKey, setActiveMetricKey] = useState(null)   // null | metric_key string
	const [metricData, setMetricData] = useState([])               // [{date, value}] for active custom metric
	const [metricsAnchorEl, setMetricsAnchorEl] = useState(null)
	const [showMetricsManager, setShowMetricsManager] = useState(false)

	useEffect(() => {
		const fetchCategories = async () => {
			const { data, error } = await getSupabase()
				.from('category')
				.select('*')
				.order('name', { ascending: true })

			if (error) {
				console.error('Error fetching categories:', error.message)
			} else {
				setCategories(data)
			}
		}

		fetchCategories()
	}, [])

	// Fetch primary data
	useEffect(() => {
		if (!dateRange[0] || !dateRange[1]) return

		const fn = async () => {
			const [start, end] = dateRange
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
			const catParam = categoryId !== 'all' ? `&categoryId=${categoryId}` : ''

			const response = await doGet(
				`/admin/reports/conversion` +
				`?startDate=${start.format('YYYY-MM-DD')}` +
				`&endDate=${end.format('YYYY-MM-DD')}` +
				`&granularity=${granularity}` +
				`&tz=${encodeURIComponent(tz)}` +
				catParam
			)

			const data = await response.json()
			setData(data)
		}
		fn()
	}, [granularity, dateRange, categoryId])

	// Fetch metric definitions
	const loadMetricDefinitions = async () => {
		try {
			const resp = await doGet('/admin/metrics')
			const data = await resp.json()
			setMetricDefinitions((data || []).filter(m => m.is_active))
		}
		catch (e) {
			console.error('Failed to load metric definitions', e)
		}
	}

	useEffect(() => {
		loadMetricDefinitions()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Deselect if the active custom metric was deleted
	useEffect(() => {
		if (!activeMetricKey || activeMetricKey === 'purchase_total') return
		const validKeys = new Set(metricDefinitions.map(m => m.metric_key))
		if (!validKeys.has(activeMetricKey)) setActiveMetricKey(null)
	}, [metricDefinitions, activeMetricKey])

	// Fetch custom metric data when selection or date range changes (skip system metric and compare mode)
	useEffect(() => {
		if (!activeMetricKey || activeMetricKey === 'purchase_total' || compareMode) {
			setMetricData([])
			return
		}
		if (!dateRange[0] || !dateRange[1]) return

		const fn = async () => {
			const [start, end] = dateRange
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone

			try {
				const resp = await doGet(
					`/admin/metrics/data` +
					`?metricKeys=${encodeURIComponent(activeMetricKey)}` +
					`&startDate=${start.format('YYYY-MM-DD')}` +
					`&endDate=${end.format('YYYY-MM-DD')}` +
					`&granularity=${granularity}` +
					`&tz=${encodeURIComponent(tz)}`
				)
				const result = await resp.json()
				setMetricData(result?.[activeMetricKey] || [])
			}
			catch (e) {
				console.error('Failed to load metric data', e)
			}
		}
		fn()
	}, [activeMetricKey, dateRange, granularity, compareMode])

	// Fetch comparison data
	useEffect(() => {
		if (!compareMode || !compareDateRange[0] || !compareDateRange[1]) return

		const fn = async () => {
			const [start, end] = compareDateRange
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
			const catParam = categoryId !== 'all' ? `&categoryId=${categoryId}` : ''

			const response = await doGet(
				`/admin/reports/conversion` +
				`?startDate=${start.format('YYYY-MM-DD')}` +
				`&endDate=${end.format('YYYY-MM-DD')}` +
				`&granularity=${granularity}` +
				`&tz=${encodeURIComponent(tz)}` +
				catParam
			)

			const data = await response.json()
			setCompareData(data)
		}
		fn()
	}, [granularity, compareDateRange, categoryId, compareMode])

	// Calculate conversion rates for each row
	const enrichedData = data.map(d => ({
		...d,
		trialConversionRate: d.registrations > 0 ? ((d.trials / d.registrations) * 100).toFixed(1) : '0.0',
		purchaseConversionRate: d.trials > 0 ? ((d.purchases / d.trials) * 100).toFixed(1) : '0.0',
		overallConversionRate: d.registrations > 0 ? ((d.purchases / d.registrations) * 100).toFixed(1) : '0.0'
	}))

	const enrichedCompareData = compareData.map(d => ({
		...d,
		trialConversionRate: d.registrations > 0 ? ((d.trials / d.registrations) * 100).toFixed(1) : '0.0',
		purchaseConversionRate: d.trials > 0 ? ((d.purchases / d.trials) * 100).toFixed(1) : '0.0',
		overallConversionRate: d.registrations > 0 ? ((d.purchases / d.registrations) * 100).toFixed(1) : '0.0'
	}))

	// Calculate totals
	const totals = data.reduce((acc, d) => ({
		registrations: acc.registrations + d.registrations,
		trials: acc.trials + d.trials,
		purchases: acc.purchases + d.purchases,
		purchaseTotal: acc.purchaseTotal + d.purchaseTotal
	}), { registrations: 0, trials: 0, purchases: 0, purchaseTotal: 0 })

	const compareTotals = compareData.reduce((acc, d) => ({
		registrations: acc.registrations + d.registrations,
		trials: acc.trials + d.trials,
		purchases: acc.purchases + d.purchases,
		purchaseTotal: acc.purchaseTotal + d.purchaseTotal
	}), { registrations: 0, trials: 0, purchases: 0, purchaseTotal: 0 })

	const totalTrialConversion = totals.registrations > 0 ? ((totals.trials / totals.registrations) * 100).toFixed(1) : '0.0'
	const totalPurchaseConversion = totals.trials > 0 ? ((totals.purchases / totals.trials) * 100).toFixed(1) : '0.0'
	const totalOverallConversion = totals.registrations > 0 ? ((totals.purchases / totals.registrations) * 100).toFixed(1) : '0.0'

	const compareTotalTrialConversion = compareTotals.registrations > 0 ? ((compareTotals.trials / compareTotals.registrations) * 100).toFixed(1) : '0.0'
	const compareTotalPurchaseConversion = compareTotals.trials > 0 ? ((compareTotals.purchases / compareTotals.trials) * 100).toFixed(1) : '0.0'
	const compareTotalOverallConversion = compareTotals.registrations > 0 ? ((compareTotals.purchases / compareTotals.registrations) * 100).toFixed(1) : '0.0'

	// Resolve active metric definition (system or custom)
	const activeMetricDef = activeMetricKey === 'purchase_total'
		? PURCHASE_TOTAL_METRIC
		: metricDefinitions.find(m => m.metric_key === activeMetricKey)

	// Dynamic right axis label
	const rightAxisLabel = activeMetricDef?.label || ''

	// Only show the right axis when something is actually plotted on it
	const rightAxisProp = compareMode
		? (selectedMetric === 'purchaseTotal' ? 'rightAxis' : null)
		: (activeMetricKey ? 'rightAxis' : null)

	// Generate chart series based on mode
	const getChartSeries = () => {
		if (!compareMode) {
			const rightSeries = []
			if (activeMetricKey === 'purchase_total') {
				rightSeries.push({
					data:     data.map(d => d.purchaseTotal),
					label:    'Purchase Total ($)',
					yAxisKey: 'rightAxis',
					color:    PURCHASE_TOTAL_METRIC.color,
				})
			} else if (activeMetricKey) {
				rightSeries.push({
					data:     data.map(d => metricData.find(m => m.date === d.date)?.value ?? null),
					label:    activeMetricDef ? activeMetricDef.label : activeMetricKey,
					yAxisKey: 'rightAxis',
					color:    activeMetricDef ? activeMetricDef.color : '#8884d8',
				})
			}

			return [
				{ data: data.map(d => d.registrations), label: 'Registrations', yAxisKey: 'leftAxis' },
				{ data: data.map(d => d.trials), label: 'Trials', yAxisKey: 'leftAxis' },
				{ data: data.map(d => d.purchases), label: 'Purchases', yAxisKey: 'leftAxis' },
				...rightSeries,
			]
		} else {
			// In compare mode, show only selected metric for both periods
			const metricLabel = selectedMetric === 'purchaseTotal' ? 'Revenue' :
				selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)
			const yAxis = selectedMetric === 'purchaseTotal' ? 'rightAxis' : 'leftAxis'

			const formatDateLabel = (start, end) => {
				return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`
			}

			// Align compare data to match primary data length
			// If lengths differ, pad with nulls or truncate
			const alignedCompareData = data.map((_, index) => {
				return compareData[index] ? compareData[index][selectedMetric] : null
			})

			return [
				{
					data: data.map(d => d[selectedMetric]),
					label: `${metricLabel} (${formatDateLabel(dateRange[0], dateRange[1])})`,
					yAxisKey: yAxis
				},
				{
					data: alignedCompareData,
					label: `${metricLabel} (${formatDateLabel(compareDateRange[0], compareDateRange[1])})`,
					yAxisKey: yAxis
				}
			]
		}
	}

	// Set compare range to "previous period" automatically
	const setPreviousPeriod = () => {
		if (!dateRange[0] || !dateRange[1]) return

		const [start, end] = dateRange
		const duration = end.diff(start, 'days')

		const prevStart = start.clone().subtract(duration + 1, 'days')
		const prevEnd = end.clone().subtract(duration + 1, 'days')

		setCompareDateRange([prevStart, prevEnd])
	}

	const getDateRangeLabel = (range) => {
		if (!range[0] || !range[1]) return 'Select dates'
		return `${range[0].format('MMM D, YYYY')} - ${range[1].format('MMM D, YYYY')}`
	}

	return (
		<LocalizationProvider dateAdapter={AdapterMoment}>
			<Container maxWidth="xl">
				<Grid item xs={12} sx={{ mb: 1 }}>
					<LeftRightCenter
						left={
							<Box display="flex" alignItems="center">
								<IconButton onClick={() => navigate(`/admin`)}>
									<ArrowBack />
								</IconButton>
								<Typography variant="h5">Conversions</Typography>
							</Box>
						}
						center={
							<FormControl>
								<Select
									size='small'
									value={categoryId}
									onChange={(e) => setCategoryId(e.target.value)}
								>
									<MenuItem key="all" value="all">All Categories</MenuItem>
									{categories.map((cat) => (
										<MenuItem key={cat.id} value={String(cat.id)}>
											{cat.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						}
						right={
							<Box display='flex' gap={2}>
								<CustomDateRangePicker
									value={dateRange}
									onChange={setDateRange}
									shortcuts={dateRangeShortcuts}
								/>
								<Select size="small" value={granularity} onChange={e => setGranularity(e.target.value)}>
									<MenuItem value="day">Day</MenuItem>
									<MenuItem value="week">Week</MenuItem>
									<MenuItem value="month">Month</MenuItem>
								</Select>
							</Box>
						}
					/>
				</Grid>

				<Grid item xs={12} sx={{ mb: 3, display: "flex", justifyContent: 'flex-end', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
					<FormControlLabel
						control={
							<Switch
								checked={compareMode}
								onChange={(e) => {
									setCompareMode(e.target.checked)
									if (e.target.checked) {
										setPreviousPeriod()
									}
								}}
							/>
						}
						label="Compare Periods"
					/>

					{compareMode && (
						<>
							<Button
								size="small"
								variant="outlined"
								onClick={setPreviousPeriod}
							>
								Previous Period
							</Button>

							<CustomDateRangePicker
								value={compareDateRange}
								onChange={setCompareDateRange}
								shortcuts={dateRangeShortcuts}
							/>

							<FormControl size="small" sx={{ minWidth: 150 }}>
								<InputLabel>Metric</InputLabel>
								<Select
									value={selectedMetric}
									onChange={(e) => setSelectedMetric(e.target.value)}
									label="Metric"
								>
									<MenuItem value="registrations">Registrations</MenuItem>
									<MenuItem value="trials">Trials</MenuItem>
									<MenuItem value="purchases">Purchases</MenuItem>
									<MenuItem value="purchaseTotal">Revenue</MenuItem>
								</Select>
							</FormControl>
						</>
					)}

					{/* Metrics toggle button */}
					<Button
						size="small"
						variant="outlined"
						startIcon={<Tune />}
						disabled={compareMode}
						onClick={(e) => setMetricsAnchorEl(e.currentTarget)}
					>
						Metrics{activeMetricKey ? ' (1)' : ''}
					</Button>

					{/* Metrics popover */}
					<Popover
						open={Boolean(metricsAnchorEl)}
						anchorEl={metricsAnchorEl}
						onClose={() => setMetricsAnchorEl(null)}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						transformOrigin={{ vertical: 'top', horizontal: 'right' }}
					>
						<Box sx={{ p: 2, minWidth: 240, maxWidth: 300 }}>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>Overlay Metric</Typography>
							<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
								Select one to overlay on the right axis.
							</Typography>
							<Stack spacing={0.5} sx={{ mb: 1 }}>
								{/* System metric */}
								{[PURCHASE_TOTAL_METRIC, ...metricDefinitions].map(def => {
									const isActive = activeMetricKey === def.metric_key
									return (
										<Box
											key={def.metric_key}
											display="flex" alignItems="center" gap={1}
											onClick={() => setActiveMetricKey(prev => prev === def.metric_key ? null : def.metric_key)}
											sx={{
												cursor: 'pointer',
												p: 0.75,
												borderRadius: 1,
												backgroundColor: isActive ? 'action.selected' : 'transparent',
												'&:hover': { backgroundColor: isActive ? 'action.selected' : 'action.hover' },
											}}
										>
											<Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: def.color, flexShrink: 0 }} />
											<Typography variant="body2" sx={{ flex: 1 }}>{def.label}</Typography>
											{isActive && <Check fontSize="small" color="primary" />}
										</Box>
									)
								})}
							</Stack>
							<Divider sx={{ my: 1 }} />
							<Button
								size="small"
								onClick={() => { setMetricsAnchorEl(null); setShowMetricsManager(true) }}
							>
								Manage Metrics
							</Button>
						</Box>
					</Popover>
				</Grid>

				{/* Line Chart */}
				<Grid item xs={12} sx={{ mb: 3 }}>
					<LineChart
						key={rightAxisProp ? 'chart-dual' : 'chart-single'}
						height={400}
						xAxis={[{
							scaleType: 'point',
							data: data.map(d => d.date)
						}]}
						yAxis={[
							{ id: 'leftAxis', label: 'Counts', width: 60 },
							...(rightAxisProp ? [{ id: 'rightAxis', label: rightAxisLabel, width: 80 }] : []),
						]}
						rightAxis={rightAxisProp}
						series={getChartSeries()}
					/>
				</Grid>

				{/* Totals Summary */}
				<Grid item xs={12} sx={{ mb: 3 }}>
					{!compareMode ? (
						<Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-around', backgroundColor: theme.palette.divider }}>
							<Box textAlign="center">
								<Typography variant="subtitle2">Registrations</Typography>
								<Typography variant="h6">{totals.registrations}</Typography>
							</Box>
							<Box textAlign="center">
								<Typography variant="subtitle2">Trials</Typography>
								<Typography variant="h6">{totals.trials}</Typography>
							</Box>
							<Box textAlign="center">
								<Typography variant="subtitle2">Purchases</Typography>
								<Typography variant="h6">{totals.purchases}</Typography>
							</Box>
							<Box textAlign="center">
								<Typography variant="subtitle2">Revenue</Typography>
								<Typography variant="h6">${totals.purchaseTotal.toFixed(2)}</Typography>
							</Box>
							<Box textAlign="center">
								<Typography variant="subtitle2">Trial Rate</Typography>
								<Typography variant="h6">{totalTrialConversion}%</Typography>
							</Box>
							<Box textAlign="center">
								<Typography variant="subtitle2">Purchase Rate</Typography>
								<Typography variant="h6">{totalPurchaseConversion}%</Typography>
							</Box>
							<Box textAlign="center">
								<Typography variant="subtitle2">Overall Rate</Typography>
								<Typography variant="h6">{totalOverallConversion}%</Typography>
							</Box>
							{activeMetricKey && activeMetricDef && (
								<Box textAlign="center">
									<Typography variant="subtitle2" sx={{ color: activeMetricDef.color }}>{activeMetricDef.label}</Typography>
									<Typography variant="h6">
										{activeMetricKey === 'purchase_total'
											? `$${totals.purchaseTotal.toFixed(2)}`
											: metricData.reduce((sum, r) => sum + Number(r.value), 0).toLocaleString()
										}
									</Typography>
								</Box>
							)}
						</Paper>
					) : (
						<Box display="flex" gap={2} flex={1} width="100%">
							<Paper sx={{ p: 2, backgroundColor: theme.palette.divider, display: 'flex', flex: 1, flexDirection: "column" }}>
								<Box textAlign="center">
									<Typography variant="subtitle2" color="success.light">{getDateRangeLabel(dateRange)}</Typography>
								</Box>
								<Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-around' }}>

									<Box textAlign="center">
										<Typography variant="subtitle2">Registrations</Typography>
										<Typography variant="h6">{totals.registrations}</Typography>
									</Box>
									<Box textAlign="center">
										<Typography variant="subtitle2">Trials</Typography>
										<Typography variant="h6">{totals.trials}</Typography>
									</Box>
									<Box textAlign="center">
										<Typography variant="subtitle2">Purchases</Typography>
										<Typography variant="h6">{totals.purchases}</Typography>
									</Box>
									<Box textAlign="center">
										<Typography variant="subtitle2">Revenue</Typography>
										<Typography variant="h6">${totals.purchaseTotal.toFixed(2)}</Typography>
									</Box>
									<Box textAlign="center">
										<Typography variant="subtitle2">Trial Rate</Typography>
										<Typography variant="h6">{totalTrialConversion}%</Typography>
									</Box>
									<Box textAlign="center">
										<Typography variant="subtitle2">Purchase Rate</Typography>
										<Typography variant="h6">{totalPurchaseConversion}%</Typography>
									</Box>
									<Box textAlign="center">
										<Typography variant="subtitle2">Overall Rate</Typography>
										<Typography variant="h6">{totalOverallConversion}%</Typography>
									</Box>
								</Box>
							</Paper>

							<Paper sx={{ p: 2, backgroundColor: theme.palette.divider, display: 'flex', flex: 1, flexDirection: "column"  }}>
								<Box textAlign="center">
									<Typography variant="subtitle2" color="info.light">{getDateRangeLabel(compareDateRange)}</Typography>
								</Box>
								<Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-around' }}>
									<Box textAlign="center">
										<Typography variant="subtitle2">Registrations</Typography>
										<Typography variant="h6">{compareTotals.registrations}</Typography>
									</Box>
									<Box textAlign="center">
										<Typography variant="subtitle2">Trials</Typography>
										<Typography variant="h6">{compareTotals.trials}</Typography>
									</Box>
									<Box textAlign="center">
										<Typography variant="subtitle2">Purchases</Typography>
										<Typography variant="h6">{compareTotals.purchases}</Typography>
									</Box>
									<Box textAlign="center">
										<Typography variant="subtitle2">Revenue</Typography>
										<Typography variant="h6">${compareTotals.purchaseTotal.toFixed(2)}</Typography>
									</Box>
									<Box textAlign="center">
										<Typography variant="subtitle2">Trial Rate</Typography>
										<Typography variant="h6">{compareTotalTrialConversion}%</Typography>
									</Box>
									<Box textAlign="center">
										<Typography variant="subtitle2">Purchase Rate</Typography>
										<Typography variant="h6">{compareTotalPurchaseConversion}%</Typography>
									</Box>
									<Box textAlign="center">
										<Typography variant="subtitle2">Overall Rate</Typography>
										<Typography variant="h6">{compareTotalOverallConversion}%</Typography>
									</Box>
								</Box>
							</Paper>
						</Box>
					)}
				</Grid>

				{/* Data Table */}
				<Grid item xs={12}>
					<Typography variant="h6" sx={{ mb: 2 }}>Detailed Data</Typography>
					{!compareMode ? (
						<TableContainer component={Paper}>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell><strong>Date</strong></TableCell>
										<TableCell align="right"><strong>Registrations</strong></TableCell>
										<TableCell align="right"><strong>Trials</strong></TableCell>
										<TableCell align="right"><strong>Purchases</strong></TableCell>
										<TableCell align="right"><strong>Revenue</strong></TableCell>
										<TableCell align="right"><strong>Trial Rate</strong></TableCell>
										<TableCell align="right"><strong>Purchase Rate</strong></TableCell>
										<TableCell align="right"><strong>Overall Rate</strong></TableCell>
										{activeMetricKey && activeMetricDef && (
											<TableCell align="right">
												<strong style={{ color: activeMetricDef.color }}>{activeMetricDef.label}</strong>
											</TableCell>
										)}
									</TableRow>
								</TableHead>
								<TableBody>
									{enrichedData.map((row) => (
										<TableRow key={row.date} hover>
											<TableCell>{row.date}</TableCell>
											<TableCell align="right">{row.registrations}</TableCell>
											<TableCell align="right">{row.trials}</TableCell>
											<TableCell align="right">{row.purchases}</TableCell>
											<TableCell align="right">${row.purchaseTotal.toFixed(2)}</TableCell>
											<TableCell align="right">{row.trialConversionRate}%</TableCell>
											<TableCell align="right">{row.purchaseConversionRate}%</TableCell>
											<TableCell align="right">{row.overallConversionRate}%</TableCell>
											{activeMetricKey && activeMetricDef && (
												<TableCell align="right">
													{activeMetricKey === 'purchase_total'
														? `$${row.purchaseTotal.toFixed(2)}`
														: (() => { const e = metricData.find(m => m.date === row.date); return e ? Number(e.value).toLocaleString() : '—' })()
													}
												</TableCell>
											)}
										</TableRow>
									))}
									<TableRow sx={{ backgroundColor: theme.palette.divider, fontWeight: 'bold' }}>
										<TableCell><strong>TOTALS</strong></TableCell>
										<TableCell align="right"><strong>{totals.registrations}</strong></TableCell>
										<TableCell align="right"><strong>{totals.trials}</strong></TableCell>
										<TableCell align="right"><strong>{totals.purchases}</strong></TableCell>
										<TableCell align="right"><strong>${totals.purchaseTotal.toFixed(2)}</strong></TableCell>
										<TableCell align="right"><strong>{totalTrialConversion}%</strong></TableCell>
										<TableCell align="right"><strong>{totalPurchaseConversion}%</strong></TableCell>
										<TableCell align="right"><strong>{totalOverallConversion}%</strong></TableCell>
										{activeMetricKey && activeMetricDef && (
											<TableCell align="right">
												<strong>
													{activeMetricKey === 'purchase_total'
														? `$${totals.purchaseTotal.toFixed(2)}`
														: metricData.reduce((sum, r) => sum + Number(r.value), 0).toLocaleString()
													}
												</strong>
											</TableCell>
										)}
									</TableRow>
								</TableBody>
							</Table>
						</TableContainer>
					) : (
						<Box display="flex" gap={2}>
							<TableContainer component={Paper} sx={{ flex: 1 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell colSpan={8} align="center" sx={{ backgroundColor: theme.palette.divider }}>
												<strong>{getDateRangeLabel(dateRange)}</strong>
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell><strong>Date</strong></TableCell>
											<TableCell align="right"><strong>Reg.</strong></TableCell>
											<TableCell align="right"><strong>Trials</strong></TableCell>
											<TableCell align="right"><strong>Purch.</strong></TableCell>
											<TableCell align="right"><strong>Rev.</strong></TableCell>
											<TableCell align="right"><strong>T.Rate</strong></TableCell>
											<TableCell align="right"><strong>P.Rate</strong></TableCell>
											<TableCell align="right"><strong>O.Rate</strong></TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{enrichedData.map((row) => (
											<TableRow key={row.date} hover>
												<TableCell>{row.date}</TableCell>
												<TableCell align="right">{row.registrations}</TableCell>
												<TableCell align="right">{row.trials}</TableCell>
												<TableCell align="right">{row.purchases}</TableCell>
												<TableCell align="right">${row.purchaseTotal.toFixed(2)}</TableCell>
												<TableCell align="right">{row.trialConversionRate}%</TableCell>
												<TableCell align="right">{row.purchaseConversionRate}%</TableCell>
												<TableCell align="right">{row.overallConversionRate}%</TableCell>
											</TableRow>
										))}
										<TableRow sx={{ backgroundColor: theme.palette.divider, fontWeight: 'bold' }}>
											<TableCell><strong>TOTALS</strong></TableCell>
											<TableCell align="right"><strong>{totals.registrations}</strong></TableCell>
											<TableCell align="right"><strong>{totals.trials}</strong></TableCell>
											<TableCell align="right"><strong>{totals.purchases}</strong></TableCell>
											<TableCell align="right"><strong>${totals.purchaseTotal.toFixed(2)}</strong></TableCell>
											<TableCell align="right"><strong>{totalTrialConversion}%</strong></TableCell>
											<TableCell align="right"><strong>{totalPurchaseConversion}%</strong></TableCell>
											<TableCell align="right"><strong>{totalOverallConversion}%</strong></TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</TableContainer>

							<TableContainer component={Paper} sx={{ flex: 1 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell colSpan={8} align="center" sx={{ backgroundColor: theme.palette.divider }}>
												<strong>{getDateRangeLabel(compareDateRange)}</strong>
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell><strong>Date</strong></TableCell>
											<TableCell align="right"><strong>Reg.</strong></TableCell>
											<TableCell align="right"><strong>Trials</strong></TableCell>
											<TableCell align="right"><strong>Purch.</strong></TableCell>
											<TableCell align="right"><strong>Rev.</strong></TableCell>
											<TableCell align="right"><strong>T.Rate</strong></TableCell>
											<TableCell align="right"><strong>P.Rate</strong></TableCell>
											<TableCell align="right"><strong>O.Rate</strong></TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{enrichedCompareData.map((row) => (
											<TableRow key={row.date} hover>
												<TableCell>{row.date}</TableCell>
												<TableCell align="right">{row.registrations}</TableCell>
												<TableCell align="right">{row.trials}</TableCell>
												<TableCell align="right">{row.purchases}</TableCell>
												<TableCell align="right">${row.purchaseTotal.toFixed(2)}</TableCell>
												<TableCell align="right">{row.trialConversionRate}%</TableCell>
												<TableCell align="right">{row.purchaseConversionRate}%</TableCell>
												<TableCell align="right">{row.overallConversionRate}%</TableCell>
											</TableRow>
										))}
										<TableRow sx={{ backgroundColor: theme.palette.divider, fontWeight: 'bold' }}>
											<TableCell><strong>TOTALS</strong></TableCell>
											<TableCell align="right"><strong>{compareTotals.registrations}</strong></TableCell>
											<TableCell align="right"><strong>{compareTotals.trials}</strong></TableCell>
											<TableCell align="right"><strong>{compareTotals.purchases}</strong></TableCell>
											<TableCell align="right"><strong>${compareTotals.purchaseTotal.toFixed(2)}</strong></TableCell>
											<TableCell align="right"><strong>{compareTotalTrialConversion}%</strong></TableCell>
											<TableCell align="right"><strong>{compareTotalPurchaseConversion}%</strong></TableCell>
											<TableCell align="right"><strong>{compareTotalOverallConversion}%</strong></TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}
				</Grid>

			</Container>

			<MetricsManager
				open={showMetricsManager}
				onClose={() => setShowMetricsManager(false)}
				onMetricsChanged={() => loadMetricDefinitions()}
			/>
		</LocalizationProvider>
	)
}

export default Conversions